import type { PayableInvoice } from '../../types/payments';
import { formatIneligibleReason } from '../../types/payments';
import { formatCurrency } from '../../lib/format';
import { formatPropertyLabel } from '../../lib/payments';

interface PayAllConfirmProps {
  /** Eligible invoices, already in due-date (run) order. */
  invoices: PayableInvoice[];
  /** Ineligible invoices at snapshot time — listed, never silently dropped. */
  skipped: PayableInvoice[];
  onConfirm: () => void;
  onCancel: () => void;
}

/** Per-currency totals, insertion-ordered by first appearance — never summed across currencies. */
function totalsByCurrency(invoices: PayableInvoice[]): Array<[string, number]> {
  const totals = new Map<string, number>();
  for (const invoice of invoices) {
    totals.set(invoice.currency, (totals.get(invoice.currency) ?? 0) + invoice.amount);
  }
  return [...totals.entries()];
}

/**
 * The pre-run confirmation for "pay all pending" (CHO-762): the eligible count
 * and the total amount per currency, plus every skipped ineligible invoice
 * with its formatted reason. Nothing has been initiated at this point — one
 * deliberate click starts the run, and each payment in it still needs its own
 * 2FA approval in the e-banking app.
 */
export function PayAllConfirm({ invoices, skipped, onConfirm, onCancel }: PayAllConfirmProps) {
  return (
    <div className="bg-white border border-gray-200 rounded-lg p-6 max-w-2xl">
      <h2 className="text-lg font-semibold text-gray-900">Pay all pending — confirm the run</h2>
      <p className="text-sm text-gray-500 mt-1">
        {invoices.length} invoice{invoices.length === 1 ? '' : 's'} will be paid in due-date order, one at a time. Each
        payment is still initiated individually and needs its own approval in your e-banking app — you approve once per
        payment on your phone; this page drives the rest. The run stops on its own if anything fails or cannot be
        confirmed.
      </p>

      <div className="mt-5 space-y-2 text-sm">
        {totalsByCurrency(invoices).map(([currency, amount]) => (
          <div key={currency} className="flex justify-between gap-4 border-t border-gray-100 pt-2">
            <span className="text-gray-500">Total in {currency}</span>
            <span className="font-semibold text-gray-900">{formatCurrency(amount, currency)}</span>
          </div>
        ))}
      </div>

      {skipped.length > 0 && (
        <div className="mt-5">
          <h3 className="text-sm font-medium text-gray-900">
            {skipped.length} invoice{skipped.length === 1 ? '' : 's'} not payable — skipped
          </h3>
          <ul className="mt-2 space-y-1 text-sm text-gray-600">
            {skipped.map((invoice) => (
              <li key={invoice.id}>
                {formatPropertyLabel(invoice)} — {invoice.billerName ?? invoice.biller ?? '—'} ·{' '}
                {formatCurrency(invoice.amount, invoice.currency)} ·{' '}
                {invoice.eligibility.reasons.map(formatIneligibleReason).join('; ') || 'Ineligible'}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="mt-6 flex items-center gap-3">
        <button
          type="button"
          onClick={onConfirm}
          className="px-4 py-2 rounded-md bg-gray-900 text-white text-sm font-medium hover:bg-gray-700"
        >
          Start the run
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 rounded-md text-sm font-medium text-gray-600 hover:bg-gray-100"
        >
          Back to queue
        </button>
      </div>
    </div>
  );
}
